// First-party cards for admin extension targets (@dunea/plugin-sdk
// primitives/admin). Dunea's own features register a card for a target here
// (custom fields on a product, reviews on an order…); <AdminExtensionSlot>
// renders these first, then the installed marketplace plugins' iframes for
// the same target. Kept free of React imports so models stay unit-testable.

import type { ComponentType } from 'react';
import type { AdminTarget } from '@dunea/plugin-sdk';

export interface AdminCardProps {
  appId: string;
  target: AdminTarget;
  /** The resource the view is showing (product id, order id…); undefined for app-level targets. */
  resourceId?: string;
}

export interface FirstPartyAdminCard {
  /** Stable id, e.g. "data.custom-fields". */
  id: string;
  title: string;
  /** Lower renders first. */
  order?: number;
  Component: ComponentType<AdminCardProps>;
}

const cards = new Map<AdminTarget, FirstPartyAdminCard[]>();

/** Register a first-party card for a target. Re-registering the same id replaces it (HMR-safe). */
export function registerAdminCard(target: AdminTarget, card: FirstPartyAdminCard): void {
  const list = (cards.get(target) ?? []).filter((c) => c.id !== card.id);

  list.push(card);
  list.sort((a, b) => (a.order ?? 100) - (b.order ?? 100));
  cards.set(target, list);
}

export function adminCardsFor(target: AdminTarget): readonly FirstPartyAdminCard[] {
  return cards.get(target) ?? [];
}
